import React, { Fragment, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { styled, useTheme } from "@mui/material/styles";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Box from "@mui/material/Box";
import { FaChevronLeft, FaChevronRight, FaChevronDown, FaChevronUp } from "react-icons/fa";
import { FaChartBar, FaMapMarkerAlt } from "react-icons/fa";
import { HiTable } from "react-icons/hi";
import { IoLeaf, IoPeople, IoSettings, IoLogOut } from "react-icons/io5";
import Header from "../components/Header/Header";

const drawerWidth = 300;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  padding: theme.spacing(1, 0, 1, 1),
  backgroundColor: "#fff",
  color: "#028e37",
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  overflowY: open ? "auto" : "hidden",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

const VALUE_CHAINS = [
  "Apiculture",
  "Avocadoes",
  "Chicken (Eggs Incubation)",
  "Chicken (Meat & Eggs)",
  "Dairy",
  "Dairy Goats",
  "Fish",
  "Pigs",
  "Tomatoes",
  "Tomato Seedlings",
];

export default function Navbar(props) {
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();
  const [open, setOpen] = useState(true);
  const [openSections, setOpenSections] = useState({
    GIS: false,
    "Farmers Data": false,
    ValueChains: false,
    Groups: false,
  });

  const handleDrawerOpen = () => setOpen(true);
  const handleDrawerClose = () => setOpen(false);

  const handleToggle = (section) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const pathname = location.pathname.split("/");

  const logout = () => {
    localStorage.clear();
    window.location.href = "/login";
  };

  const menuItems = [
    { text: "Statistics", icon: FaChartBar, path: "/" },
    {
      text: "GIS",
      icon: FaMapMarkerAlt,
      subItems: [
        { text: "All Farmers", path: "/gis/All%20Farmers" },
        { text: "Fertilizer Farmers", path: "/gis/Fertilizer%20Farmers" },
        { text: "Profiled Farmers", path: "/gis/Profiled%20Farmers" },
      ],
    },
    {
      text: "Farmers Data",
      icon: HiTable,
      subItems: [
        { text: "All Farmers", path: "/data/All%20Farmers" },
        { text: "Fertilizer Farmers", path: "/data/Fertilizer%20Farmers" },
      ],
    },
    {
      text: "ValueChains",
      icon: IoLeaf,
      subItems: VALUE_CHAINS.map((vc) => ({
        text: vc,
        path: `/valuechains/${encodeURIComponent(vc)}`,
      })),
    },
    {
      text: "Groups",
      icon: IoPeople,
      subItems: [
        { text: "List", path: "/groups/List" },
        { text: "New", path: "/groups/New" },
      ],
    },
  ];

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar position="fixed" open={open}>
        <Toolbar>
          <Header
            handleDrawerOpen={handleDrawerOpen}
            open={open}
          />
        </Toolbar>
      </AppBar>
      <Drawer variant="permanent" open={open}>
        <DrawerHeader>
          <Box />
          <IconButton onClick={handleDrawerClose} sx={{ color: "#028e37" }}>
            {theme.direction === "rtl" ? <FaChevronRight size={20} /> : <FaChevronLeft size={20} />}
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List>
          {menuItems.map((item) => (
            <Fragment key={item.text}>
              {item.subItems ? (
                <>
                  <ListItem button onClick={() => handleToggle(item.text)}>
                    <ListItemIcon sx={{ color: "primary.main", minWidth: 40 }}>
                      {(() => {
                        const Icon = item.icon;
                        return <Icon size={20} />;
                      })()}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      sx={{
                        fontSize: "small",
                        color: "text.primary",
                        fontWeight: "normal",
                        ...(!open && { opacity: 0, width: 0, minWidth: 0, overflow: "hidden", padding: 0 }),
                      }}
                    />
                    {open && (openSections[item.text] ? <FaChevronUp size={18} /> : <FaChevronDown size={18} />)}
                  </ListItem>
                  {open && openSections[item.text] &&
                    item.subItems.map((sub) => (
                      <ListItem
                        key={sub.text}
                        button
                        onClick={() => history.push(sub.path)}
                        selected={location.pathname === sub.path}
                        sx={{
                          pl: 4,
                          bgcolor:
                            location.pathname === sub.path
                              ? "action.selected"
                              : "transparent",
                        }}
                      >
                        <ListItemText
                          primary={sub.text}
                          sx={{
                            fontSize: "small",
                            ...(!open && { opacity: 0, width: 0, minWidth: 0, overflow: "hidden", padding: 0 }),
                          }}
                        />
                      </ListItem>
                    ))}
                </>
              ) : (
                <ListItem
                  button
                  onClick={() => history.push(item.path)}
                  selected={location.pathname === item.path || (item.path === "/" && pathname[1] === "")}
                  sx={{
                    bgcolor:
                      location.pathname === item.path || (item.path === "/" && !pathname[1])
                        ? "action.selected"
                        : "transparent",
                  }}
                >
                  <ListItemIcon sx={{ color: "primary.main", minWidth: 40 }}>
                    {(() => {
                      const Icon = item.icon;
                      return <Icon size={20} />;
                    })()}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    sx={{
                      fontSize: "small",
                      color: "text.primary",
                      fontWeight:
                        location.pathname === item.path || (item.path === "/" && !pathname[1])
                          ? "bold"
                          : "normal",
                      ...(!open && { opacity: 0, width: 0, minWidth: 0, overflow: "hidden", padding: 0 }),
                    }}
                  />
                </ListItem>
              )}
            </Fragment>
          ))}
        </List>
        <Divider />
        <List>
          <ListItem
            button
            onClick={() => history.push("/settings")}
            selected={pathname[1] === "settings"}
            sx={{
              bgcolor: pathname[1] === "settings" ? "action.selected" : "transparent",
            }}
          >
            <ListItemIcon sx={{ color: "primary.main", minWidth: 40 }}>
              <IoSettings size={20} />
            </ListItemIcon>
            <ListItemText
              primary="Settings"
              sx={{
                fontSize: "small",
                ...(!open && { opacity: 0, width: 0, minWidth: 0, overflow: "hidden", padding: 0 }),
              }}
            />
          </ListItem>
          <ListItem button onClick={logout}>
            <ListItemIcon sx={{ color: "primary.main", minWidth: 40 }}>
              <IoLogOut size={20} />
            </ListItemIcon>
            <ListItemText
              primary="Logout"
              sx={{
                fontSize: "small",
                ...(!open && { opacity: 0, width: 0, minWidth: 0, overflow: "hidden", padding: 0 }),
              }}
            />
          </ListItem>
        </List>
      </Drawer>
    </Box>
  );
}
